import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import MainDialogLayout from '@/components/ui/MainDialogLayout/MainDialogLayout';
import Button from '@/components/ui/Button';
import Input from '@/components/ui/Input';
import { FundManagementDetailsDtoIn, FundManagementSupported } from '@/models/in/FundManagementDetailsDtoIn';
import editIcon from '@/assets/icons/edit.svg';
import deleteIcon from '@/assets/icons/delete.svg';
import addIcon from '@/assets/icons/add.svg';
import { toLocalPhone } from '@/helpers/phoneHelper';
import { buildUpdateDtoFromFund, emptySupported, validateSupporteds } from './fundViewForm';
import useUpdateFund from './useUpdateFund';
import styles from './FundDialogs.module.css';
import { toHebrewName } from '@/helpers/hebrewName';

interface EditSupportedsDialogProps {
  open: boolean;
  onClose: () => void;
  fund: FundManagementDetailsDtoIn;
}

const EditSupportedsDialog = ({ open, onClose, fund }: EditSupportedsDialogProps) => {
  const { t } = useTranslation();
  const [supporteds, setSupporteds] = useState<FundManagementSupported[]>([]);
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [note, setNote] = useState('');
  const [validationError, setValidationError] = useState<string | null>(null);

  const { save, isSaving, serverError, clearServerError } = useUpdateFund(String(fund.id), onClose);

  useEffect(() => {
    if (open) {
      // Phones are stored in E.164; edit them in the local form people typed. The server
      // re-normalizes on save, so what goes back out is unchanged in shape.
      setSupporteds((fund.supporteds ?? []).map((supported) => ({
        ...supported,
        phone: toLocalPhone(supported.phone),
      })));
      setEditingIndex(null);
      setNote('');
      setValidationError(null);
      clearServerError();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, fund]);

  const updateSupported = (index: number, changes: Partial<FundManagementSupported>) => {
    setSupporteds(supporteds.map((supported, i) => (i === index ? { ...supported, ...changes } : supported)));
  };

  const removeSupported = (index: number) => {
    setSupporteds(supporteds.filter((_, i) => i !== index));
    setEditingIndex(null);
  };

  const addSupported = () => {
    setSupporteds([...supporteds, emptySupported()]);
    setEditingIndex(supporteds.length);
  };

  const handleSave = () => {
    const error = validateSupporteds(supporteds);
    if (error) {
      setValidationError(error);
      return;
    }
    if (!note.trim()) {
      setValidationError('fundsManagement.noteDialog.noteRequired');
      return;
    }
    setValidationError(null);
    save(buildUpdateDtoFromFund(fund, {
      supporteds: supporteds.map((supported) => ({
        ...supported,
        israeliId: supported.israeliId.trim(),
        firstName: supported.firstName.trim(),
        lastName: supported.lastName.trim(),
      })),
    }, note.trim()));
  };

  const displayName = (supported: FundManagementSupported) =>
    `${supported.firstName} ${supported.lastName}`.trim() || t('fundsManagement.newSupported');

  const summaryLine = (supported: FundManagementSupported) => {
    const street = [supported.street, supported.houseNumber].filter(Boolean).join(' ');
    const address = [street, supported.city].filter(Boolean).join(', ');
    return [supported.israeliId, address, supported.phone, supported.email].filter(Boolean).join(' • ');
  };

  return (
    <MainDialogLayout
      open={open}
      onOpenChange={(isOpen: boolean) => { if (!isOpen) onClose(); }}
      title={t('fundsManagement.editSupportedsTitle')}
      contentClassName={styles.wideDialogContent}
    >
      <div className={styles.dialogForm}>
        {supporteds.length === 0 && (
          <p className={styles.panelSubtitle}>{t('fundsManagement.noSupporteds')}</p>
        )}

        <div className={styles.peopleList}>
          {supporteds.map((supported, index) => (
            editingIndex === index ? (
              <div key={index} className={styles.dialogListItem}>
                <div className={styles.dialogListItemHeader}>
                  <span className={styles.personName}>{displayName(supported)}</span>
                  <button
                    type="button"
                    className={styles.removeBtn}
                    aria-label={t('fundsManagement.remove')}
                    onClick={() => removeSupported(index)}
                  >
                    <img src={deleteIcon} alt="" />
                  </button>
                </div>
                <div className={styles.fieldsGrid}>
                  <div className={styles.formField}>
                    <label className={styles.fieldLabel}>{t('fundsManagement.israeliId')}</label>
                    <Input dir="ltr" value={supported.israeliId} onChange={(e) => updateSupported(index, { israeliId: e.target.value })} />
                  </div>
                  <div className={styles.formField}>
                    <label className={styles.fieldLabel}>{t('fundsManagement.firstName')}</label>
                    <Input value={supported.firstName} onChange={(e) => updateSupported(index, { firstName: toHebrewName(e.target.value) })} />
                  </div>
                  <div className={styles.formField}>
                    <label className={styles.fieldLabel}>{t('fundsManagement.lastName')}</label>
                    <Input value={supported.lastName} onChange={(e) => updateSupported(index, { lastName: toHebrewName(e.target.value) })} />
                  </div>
                  <div className={styles.formField}>
                    <label className={styles.fieldLabel}>{t('fundsManagement.city')}</label>
                    <Input value={supported.city ?? ''} onChange={(e) => updateSupported(index, { city: e.target.value })} />
                  </div>
                  <div className={styles.formField}>
                    <label className={styles.fieldLabel}>{t('fundsManagement.street')}</label>
                    <Input value={supported.street ?? ''} onChange={(e) => updateSupported(index, { street: e.target.value })} />
                  </div>
                  <div className={styles.formField}>
                    <label className={styles.fieldLabel}>{t('fundsManagement.houseNumber')}</label>
                    <Input value={supported.houseNumber ?? ''} onChange={(e) => updateSupported(index, { houseNumber: e.target.value })} />
                  </div>
                  <div className={styles.formField}>
                    <label className={styles.fieldLabel}>{t('fundsManagement.phone')}</label>
                    <Input dir="ltr" value={supported.phone ?? ''} onChange={(e) => updateSupported(index, { phone: e.target.value })} />
                  </div>
                  <div className={styles.formField}>
                    <label className={styles.fieldLabel}>{t('fundsManagement.email')}</label>
                    <Input dir="ltr" value={supported.email ?? ''} onChange={(e) => updateSupported(index, { email: e.target.value })} />
                  </div>
                </div>
                <Button variant="white" className="self-start" onClick={() => setEditingIndex(null)}>
                  {t('fundsManagement.done')}
                </Button>
              </div>
            ) : (
              <div key={index} className={styles.personListRow}>
                <div className={styles.personSummary}>
                  <span className={styles.personName}>{displayName(supported)}</span>
                  <span className={styles.personMetaLine}>{summaryLine(supported)}</span>
                </div>
                <div className={styles.rowActions}>
                  <button type="button" className={styles.rowEditBtn} onClick={() => setEditingIndex(index)}>
                    <img src={editIcon} alt="" />
                    {t('fundsManagement.editButton')}
                  </button>
                  <button
                    type="button"
                    className={styles.removeBtn}
                    aria-label={t('fundsManagement.remove')}
                    onClick={() => removeSupported(index)}
                  >
                    <img src={deleteIcon} alt="" />
                  </button>
                </div>
              </div>
            )
          ))}
        </div>

        <Button variant="outline" className={`self-start !inline-flex items-center gap-1.5 ${styles.addBtn}`} onClick={addSupported}>
          <img src={addIcon} alt="" />
          {t('fundsManagement.addSupported')}
        </Button>

        <div className={styles.formField}>
          <label className={styles.fieldLabel}>{t('fundsManagement.noteDialog.noteLabel')}</label>
          <textarea
            className={styles.dialogTextarea}
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder={t('fundsManagement.noteDialog.notePlaceholder')}
          />
        </div>

        {validationError && <p className={styles.formError}>{t(validationError)}</p>}
        {serverError && <p className={styles.formError}>{serverError}</p>}

        <div className={styles.formActions}>
          <Button variant="white" onClick={onClose} disabled={isSaving}>
            {t('fundsManagement.cancel')}
          </Button>
          <Button variant="primary" onClick={handleSave} disabled={isSaving}>
            {isSaving ? t('loading') : t('fundsManagement.save')}
          </Button>
        </div>
      </div>
    </MainDialogLayout>
  );
};

export default EditSupportedsDialog;
